import { hasOwnProperty } from "@glideapps/ts-necessities";

import type {
    AppFacilities,
    UploadProgressHandler,
    UploadSession as IUploadSession,
    UploadFileResponseError,
    UploadFileResponse,
} from "./types";
import { isResponseOK, assert, checkString, dontAwait, assertNever, sleep, exceptionToError } from "../Support";
import type { UploadAppFileV2Body, UploadComponentKind } from "../firebase-function-types";
import { logError } from "../debug-print";
import { getFeatureFlag } from "../feature-flags";

export class CancelledError extends Error {
    constructor() {
        super("Operation was cancelled");
    }
}

export class NetworkOfflineError extends Error {
    constructor() {
        super("The network is not available");
    }
}

export class RemoteResponseError extends Error {
    constructor(statusCode: number) {
        super(`Upload failed with status code ${statusCode}`);
    }
}

export class InsecureUploadError extends Error {
    constructor(endpoint: string) {
        super(`Would not upload data to ${endpoint}`);
    }
}

export class FileReadError extends Error {
    constructor(fileName: string) {
        super(`Could not read ${fileName}`);
    }
}

export class FileSizeLimitExceededError extends Error {
    constructor(public givenSize: number, public maxSize: number) {
        super(`File size ${givenSize} exceeded maximum size ${maxSize}`);
    }
}

function errorForBadResponse(resp: Response | undefined): UploadFileResponseError {
    return {
        error: resp === undefined ? new NetworkOfflineError() : new RemoteResponseError(resp.status),
        quotaExceeded: resp?.status === 402,
    };
}

type UploadState = "request-upload" | "uploading-direct" | "reading-file" | "uploading-read" | "finish-upload";
type CancellableResponse<T> = Readonly<{
    cancel: () => void;
    responsePromise: Promise<T>;
}>;

type XHRResponse =
    | "unsupported-body"
    | "cancelled"
    | "timeout"
    | "error"
    | { remoteStatus: number; receivedBytes?: number };
type XHRUpload = CancellableResponse<XHRResponse>;

function fileSizeForBuffer(blob: File | ArrayBuffer): number {
    return blob instanceof File ? blob.size : blob.byteLength;
}

function extractReceivedBytesFromRangeHeader(header: string | null) {
    if (header === null) return 0;
    const matched = /bytes=0-([0-9]+)/.exec(header);
    if (matched === null || matched[1] === undefined) return 0;
    try {
        return Number.parseInt(matched[1], 10) + 1;
    } catch {
        return 0;
    }
}

function initializeXHRForUpload(
    uploadPath: string,
    blob: File | ArrayBuffer,
    startingOffset: number,
    maximumSize: number,
    onProgress: UploadProgressHandler | undefined
): XHRUpload {
    const xhr = new XMLHttpRequest();
    const fileSize = fileSizeForBuffer(blob);
    xhr.upload.onprogress = e => {
        if (onProgress === undefined) return;
        onProgress(fileSize, startingOffset + e.loaded);
    };
    const cancel = () => xhr.abort();
    const upperBound = Math.min(startingOffset + maximumSize, fileSize) - 1;
    const responsePromise = new Promise<XHRResponse>(resolve => {
        let resolvedOnce = false;
        xhr.ontimeout = () => {
            if (resolvedOnce) return;
            resolvedOnce = true;
            resolve("timeout");
        };
        xhr.onerror = () => {
            if (resolvedOnce) return;
            resolvedOnce = true;
            resolve("error");
        };
        xhr.onabort = () => {
            if (resolvedOnce) return;
            resolvedOnce = true;
            resolve("cancelled");
        };
        xhr.onreadystatechange = () => {
            if (xhr.readyState === XMLHttpRequest.DONE && !resolvedOnce) {
                resolvedOnce = true;
                if (xhr.status === 0) {
                    resolve("error");
                } else {
                    const receivedBytes = extractReceivedBytesFromRangeHeader(xhr.getResponseHeader("range"));
                    resolve({ remoteStatus: xhr.status, receivedBytes });
                }
            }
        };
    });
    xhr.open("PUT", uploadPath, true);
    xhr.setRequestHeader("Content-Type", "application/octet-stream");
    xhr.setRequestHeader("Content-Range", `bytes ${startingOffset}-${upperBound}/${fileSize}`);
    try {
        xhr.send(blob.slice(startingOffset, upperBound + 1));
        return {
            cancel,
            responsePromise,
        };
    } catch {
        // It's unclear exactly when iOS started supporting Blob inputs
        // in XMLHttpResponse, but iOS 13.6 definitely does. As of 2020-08-11
        // MDN was showing question marks across the board about XHR.send
        // supporting anything other than ArrayBuffer in any Safari.
        // For that matter it was showing question marks about sending
        // anything other than UVString in iOS, but we know that ArrayBuffer
        // definitely works on iOS 12.
        return {
            cancel,
            responsePromise: Promise.resolve("unsupported-body"),
        };
    }
}

const lowestBackoffTimeMS = 500;
const maxFailures = 5;

// FIXME: This should probably be standard for all known-idempotent
// Cloud Function calls.
async function runWithRetryBackoff<T>(
    fn: () => Promise<T | undefined>,
    desc: string,
    resultIsOK?: (r: T) => boolean
): Promise<T | undefined> {
    let sleepTime = 0;
    let consecutiveFailures = 0;
    while (consecutiveFailures < maxFailures) {
        await sleep(sleepTime);
        try {
            const response = await fn();
            if (response !== undefined && (resultIsOK === undefined || resultIsOK(response))) {
                return response;
            }
        } catch (e: unknown) {
            logError(`Error when ${desc}`, e);
        }
        sleepTime = Math.pow(2, consecutiveFailures) * lowestBackoffTimeMS;
        consecutiveFailures++;
    }
    return undefined;
}

function responseOKNot500(r: Response): boolean {
    return Math.floor(r.status / 100) !== 5;
}

async function runCloudFunctionWithRetryBackoff(
    appFacilities: AppFacilities,
    cloudFunction: string,
    body: any,
    headers: Record<string, string> = {}
): Promise<Response | undefined> {
    const fetchFunction = async () => {
        if (getFeatureFlag("injectFileUploadFaults") && Math.random() < 0.25) return undefined;
        return appFacilities.callAuthIfAvailableCloudFunction(cloudFunction, body, headers);
    };
    return runWithRetryBackoff(fetchFunction, `calling ${cloudFunction}`, responseOKNot500);
}

async function pollForFileUploadStatus(uploadPath: string, fileSize: number): Promise<number | undefined> {
    const fetchFunction = async () => {
        if (getFeatureFlag("injectFileUploadFaults") && Math.random() < 0.25) return undefined;
        const abortController = new AbortController();

        const response = await fetch(uploadPath, {
            method: "PUT",
            body: null,
            headers: { "Content-Range": `bytes */${fileSize}` },
            signal: abortController.signal,
        });
        abortController.abort();
        return response;
    };
    const fetchIsOK = (r: Response) => {
        const ok = r.status === 200 || r.status === 201 || r.status === 308;
        if (!ok) {
            logError("Polling for upload status", r.statusText);
        }
        return ok;
    };
    const pollResponse = await runWithRetryBackoff(fetchFunction, "polling for upload status", fetchIsOK);
    if (pollResponse === undefined) return undefined;
    if (pollResponse.status === 308) return extractReceivedBytesFromRangeHeader(pollResponse.headers.get("range"));
    return fileSize;
}

function withInternalCancellation<T>(
    func: (setInternalCancel: (internalCancel: () => void) => void, isCancelled: () => boolean) => Promise<T>
): CancellableResponse<T> {
    let cancelInternal: (() => void) | undefined;
    let cancelled = false;
    const cancelExternal = () => {
        if (cancelled) return;
        cancelled = true;
        if (cancelInternal !== undefined) {
            cancelInternal();
        }
    };
    return {
        cancel: cancelExternal,
        responsePromise: func(
            cancel => {
                cancelInternal = cancel;
            },
            () => cancelled
        ),
    };
}

// Chunk size must be some multiple of 256 KB except for the last chunk.
const baseChunkSize = 256 * 1024;
const maximumChunks = 32;

function withUploadRetryLoop(
    uploadPath: string,
    blob: File | ArrayBuffer,
    elideFirstStatusPoll: boolean,
    onProgress: UploadProgressHandler | undefined
): XHRUpload {
    return withInternalCancellation(async (setInternalCancel, isCancelled) => {
        const fileSize = fileSizeForBuffer(blob);

        let needsStatusPoll = !elideFirstStatusPoll;
        let sleepTime = 0;
        let consecutiveFailures = 0;
        let consecutiveFailuresForSleep = 0;
        let startOffset = 0;
        let chunkCount = 1;

        const handleFailure = (resetSleep: boolean = false) => {
            if (resetSleep) {
                sleepTime = consecutiveFailuresForSleep = 0;
            } else {
                sleepTime = Math.pow(2, consecutiveFailuresForSleep) * lowestBackoffTimeMS;
                consecutiveFailuresForSleep++;
            }
            consecutiveFailures++;
            chunkCount = 1;
        };

        uploadLoop: while (true) {
            const chunkSize = chunkCount * baseChunkSize;
            if (getFeatureFlag("injectFileUploadFaults") && Math.random() < 0.25) {
                const roll = Math.random();
                if (roll < 0.33) {
                    return "timeout";
                } else if (roll < 0.66) {
                    return "error";
                } else if (blob instanceof File) {
                    return "unsupported-body";
                } else {
                    return { remoteStatus: 503 };
                }
            }

            if (needsStatusPoll) {
                const maybeStartOffset = await pollForFileUploadStatus(uploadPath, fileSize);
                if (maybeStartOffset === undefined) return "error";
                startOffset = maybeStartOffset;
            }
            needsStatusPoll = true;
            if (isCancelled()) return "cancelled";
            if (startOffset >= fileSize) return { remoteStatus: 200 };

            const xhrUpload = initializeXHRForUpload(uploadPath, blob, startOffset, chunkSize, onProgress);
            setInternalCancel(xhrUpload.cancel);

            const xhrResponse = await xhrUpload.responsePromise;
            if (typeof xhrResponse !== "string") {
                const { remoteStatus, receivedBytes } = xhrResponse;
                const firstDigit = Math.floor(remoteStatus / 100);
                if (firstDigit === 5) {
                    handleFailure();

                    // ESLint has very bizarre standards for clarity out of the box.
                    // Apparently this is "bad" because it's "unnecessary".
                    // eslint-disable-next-line no-extra-label
                    continue uploadLoop;
                } else if (firstDigit === 2 && startOffset + chunkSize < fileSize) {
                    startOffset += chunkSize;
                } else if (remoteStatus !== 308) {
                    // HTTP 308 means we have to continue with the upload.
                    // The docs aren't clear on this, but it definitely happens
                    // for every partial PUT request.
                    //
                    // Anything other than a 2xx, 308, or 5xx isn't something
                    // we should expect to recover from.
                    return xhrResponse;
                }
                if (receivedBytes !== undefined) {
                    startOffset = receivedBytes;
                    needsStatusPoll = false;
                }

                sleepTime = consecutiveFailures = consecutiveFailuresForSleep = 0;
                chunkCount = Math.min(maximumChunks, chunkCount * 2);
            } else {
                // XMLHttpRequest abort, error, and timeout handlers can all race
                // with each other. We actually see "error" before "abort" on Firefox.
                // So we need to check whether we explicitly cancelled the operation.
                if (isCancelled()) return "cancelled";
                switch (xhrResponse) {
                    case "timeout":
                        // "timeout" differs from "error" in the sense that
                        // we've already implicitly slept. So we can reset
                        // the sleep counter, but it's still a consecutive failure.
                        handleFailure(true);
                        break;
                    case "error":
                        handleFailure(true);
                        break;
                    case "cancelled":
                    // fallthrough
                    case "unsupported-body":
                        return xhrResponse;
                    default:
                        return assertNever(xhrResponse);
                }
            }

            if (consecutiveFailures > maxFailures) return "error";
            await sleep(sleepTime);
        }
    });
}

function isValidUploadLocation(uploadLocation: string) {
    return (
        uploadLocation.startsWith(window.location.origin + "/") ||
        uploadLocation.startsWith("https://storage.googleapis.com/") ||
        uploadLocation.startsWith("https://upload.glideapps.com/") ||
        uploadLocation.startsWith("https://upload.heyglide.com/")
    );
}

const cancelledError = {
    error: new CancelledError(),
    quotaExceeded: false,
};

interface UploadIdentity {
    uploadID: string;
    uploadLocation: string;
}

function isUploadIdentity(x: unknown): x is UploadIdentity {
    return hasOwnProperty(x, "uploadID") && hasOwnProperty(x, "uploadLocation");
}

class UploadSession implements IUploadSession {
    private fallbackBuffer: ArrayBuffer | undefined;
    private needsConfirmedQuery = false;
    private uploadState: UploadState = "request-upload";
    private uploadIdentity: { uploadID: string; uploadLocation: string } | undefined;
    private isCancelledDirect = false;
    private isCancelled: (() => boolean) | undefined;
    private cancellationFunc: (() => void) | undefined;

    constructor(
        private appFacilities: AppFacilities,
        private readonly appID: string,
        private readonly file: File,
        private readonly component: UploadComponentKind,
        private readonly onProgress: UploadProgressHandler | undefined,
        private readonly onAttemptResponse?: (resp: UploadFileResponse) => void
    ) {}

    private checkIfCancelled(): boolean {
        let isCancelled = this.isCancelledDirect;
        if (this.isCancelled !== undefined) {
            isCancelled = isCancelled || this.isCancelled();
        }
        // We need to make sure the fallback buffer is undefined on cancellation,
        // otherwise that's a memory leak.
        if (isCancelled) {
            this.fallbackBuffer = undefined;
        }
        return isCancelled;
    }

    public cancel(): void {
        if (this.checkIfCancelled()) return;
        this.isCancelledDirect = true;
        // Clear the fallback buffer on cancellation, we won't need it.
        this.fallbackBuffer = undefined;
        if (this.cancellationFunc !== undefined) {
            this.cancellationFunc();
        }
    }

    private reportUploadError(error: Error) {
        if (this.uploadIdentity !== undefined) {
            const { uploadID } = this.uploadIdentity;
            dontAwait(
                runCloudFunctionWithRetryBackoff(this.appFacilities, "uploadAppFileV2", {
                    operation: "report-failure",
                    uploadID,
                    permanent: true,
                    kind: `${this.uploadState}-exception`,
                    message: `${error}`,
                })
            );
        }
    }

    private async updatingInternalCancellation<T>(
        func: (setInternalCancel: (internalCancel: () => void) => void) => Promise<T>
    ): Promise<T> {
        const { cancel, responsePromise } = withInternalCancellation(async (setInternalCancel, isCancelled) => {
            this.isCancelled = isCancelled;
            return func(setInternalCancel);
        });
        this.cancellationFunc = cancel;
        try {
            return await responsePromise;
        } finally {
            this.cancellationFunc = undefined;
        }
    }

    private async uploadToStorageEndpoint(uploadPath: string): Promise<XHRResponse> {
        return this.updatingInternalCancellation(async setInternalCancel => {
            if (this.fallbackBuffer === undefined) {
                this.uploadState = "uploading-direct";
                const nonReading = withUploadRetryLoop(
                    uploadPath,
                    this.file,
                    !this.needsConfirmedQuery,
                    this.onProgress
                );

                setInternalCancel(nonReading.cancel);
                const nonReadingResult = await nonReading.responsePromise;
                this.needsConfirmedQuery = true;

                if (this.checkIfCancelled()) return "cancelled";
                if (nonReadingResult !== "unsupported-body") return nonReadingResult;

                this.uploadState = "reading-file";
                if (getFeatureFlag("injectFileUploadFaults") && Math.random() < 0.5) {
                    return "unsupported-body";
                }
                try {
                    this.fallbackBuffer = await new Promise<ArrayBuffer>((resolve, reject) => {
                        const reader = new FileReader();

                        const err = (ev: ProgressEvent<FileReader>) => {
                            if (ev.target?.error !== null && ev.target?.error !== undefined) {
                                reject(ev.target.error);
                            } else {
                                reject(new FileReadError(this.file.name));
                            }
                        };

                        const success = (ev: ProgressEvent<FileReader>) => {
                            try {
                                assert(ev.target?.result instanceof ArrayBuffer);
                                resolve(ev.target.result);
                            } catch (e: unknown) {
                                reject(e);
                            }
                        };

                        reader.onabort = err;
                        reader.onerror = err;
                        reader.onloadend = success;
                        reader.readAsArrayBuffer(this.file);
                    });
                } catch {
                    return this.checkIfCancelled() ? "cancelled" : "unsupported-body";
                }

                if (this.checkIfCancelled()) return "cancelled";
            }

            this.uploadState = "uploading-read";
            const reading = withUploadRetryLoop(uploadPath, this.fallbackBuffer, false, this.onProgress);

            setInternalCancel(reading.cancel);
            const readingResult = await reading.responsePromise;

            return this.checkIfCancelled() ? "cancelled" : readingResult;
        });
    }

    private async beginUploadProcess(): Promise<UploadFileResponse | UploadIdentity> {
        if (this.uploadIdentity !== undefined) return this.uploadIdentity;

        return this.updatingInternalCancellation(async () => {
            const { file } = this;

            const contentLength = file.size;
            const contentType = file.type;
            const lastDot = file.name.lastIndexOf(".");
            const proposedExtension = lastDot < 0 ? undefined : file.name.substring(lastDot + 1);

            const beginBody: UploadAppFileV2Body = {
                operation: "begin",
                appID: this.appID,
                contentType,
                contentLength,
                origin: window.location.origin,
                proposedExtension,
                component: this.component,
            };
            const initiatingResponse = await runCloudFunctionWithRetryBackoff(
                this.appFacilities,
                "uploadAppFileV2",
                beginBody
            );
            if (!isResponseOK(initiatingResponse) && initiatingResponse?.status !== 413) {
                return errorForBadResponse(initiatingResponse);
            }

            try {
                const initiatingPayload = await initiatingResponse.json();

                if (initiatingResponse.status === 413) {
                    // We have gone over the file size limit for this app.
                    // The body will tell us how much we could have uploaded.
                    const { maxUploadSize } = initiatingPayload;
                    return {
                        error: new FileSizeLimitExceededError(contentLength, maxUploadSize),
                        quotaExceeded: false,
                    };
                }

                const { uploadLocation: responseUploadLocation } = initiatingPayload;
                const uploadID = initiatingPayload.uploadID;

                assert(typeof uploadID === "string", "uploadID was not string");
                const uploadLocation = responseUploadLocation;
                assert(typeof uploadLocation === "string", "uploadLocation was not a string");
                this.uploadIdentity = { uploadID, uploadLocation };
            } catch (error: unknown) {
                this.reportUploadError(exceptionToError(error));
                if (this.checkIfCancelled()) return cancelledError;
                return {
                    error: exceptionToError(error),
                    quotaExceeded: false,
                };
            }

            if (this.checkIfCancelled()) return cancelledError;

            const { uploadLocation } = this.uploadIdentity;

            if (!isValidUploadLocation(uploadLocation)) {
                this.uploadIdentity = undefined;
                const error = new InsecureUploadError(uploadLocation);
                this.reportUploadError(error);
                return {
                    error,
                    quotaExceeded: false,
                };
            }
            return this.uploadIdentity;
        });
    }

    private async attemptInternal(): Promise<UploadFileResponse> {
        if (this.checkIfCancelled()) return cancelledError;

        const uploadIdentity = await this.beginUploadProcess();
        if (!isUploadIdentity(uploadIdentity)) {
            return uploadIdentity;
        }
        if (this.checkIfCancelled()) return cancelledError;

        const { uploadID, uploadLocation } = uploadIdentity;

        const uploadResponse = await this.uploadToStorageEndpoint(uploadLocation);
        if (this.checkIfCancelled()) return cancelledError;

        if (typeof uploadResponse === "string") {
            if (uploadResponse === "cancelled") return cancelledError;

            const error =
                uploadResponse === "unsupported-body" ? new FileReadError(this.file.name) : new NetworkOfflineError();
            this.reportUploadError(error);
            return {
                error,
                quotaExceeded: false,
            };
        }
        const { remoteStatus } = uploadResponse;
        if (Math.floor(remoteStatus / 100) !== 2) {
            const error = new RemoteResponseError(remoteStatus);
            this.reportUploadError(error);
            return {
                error,
                quotaExceeded: false,
            };
        }

        this.uploadState = "finish-upload";
        const completeBody: UploadAppFileV2Body = {
            operation: "complete",
            uploadID,
        };
        const confirmingResponse = await runCloudFunctionWithRetryBackoff(
            this.appFacilities,
            "uploadAppFileV2",
            completeBody
        );
        if (!isResponseOK(confirmingResponse)) return errorForBadResponse(confirmingResponse);

        try {
            const { path } = await confirmingResponse.json();
            if (this.checkIfCancelled()) return cancelledError;
            return { path: checkString(path) };
        } catch (error: unknown) {
            this.reportUploadError(exceptionToError(error));
            return {
                error: exceptionToError(error),
                quotaExceeded: false,
            };
        }
    }

    public async attempt(): Promise<UploadFileResponse> {
        const resp = await this.attemptInternal();
        if (this.onAttemptResponse !== undefined) {
            this.onAttemptResponse(resp);
        }
        return resp;
    }
}

export function uploadFileIntoGlideStorage(
    appFacilities: AppFacilities,
    appID: string,
    file: File,
    component: UploadComponentKind,
    onProgress: UploadProgressHandler | undefined,
    onAttemptResponse?: (resp: UploadFileResponse) => void
): UploadSession {
    return new UploadSession(appFacilities, appID, file, component, onProgress, onAttemptResponse);
}
